import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { faBriefcase } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";

import { Icon } from "@/components/Icon/Icon";
import { Toggle } from "@/components/Toggle/Toggle";
import { SelectField, SelectFieldValue } from "@/components/SelectField/SelectField";

import {
	JOB_CHANGE_LANGUAGES,
	JobChangeLanguage,
	LANGUAGE_LABELS,
	LANGUAGE_PHRASES,
} from "./jobChangeLanguages";

interface JobChangeAnnouncementProps {
	enabled: boolean;
	language: JobChangeLanguage;
	onToggle: () => void;
	onLanguageChange: (language: JobChangeLanguage) => void;
}

export const JobChangeAnnouncement: FunctionComponent<JobChangeAnnouncementProps> = ({
	enabled,
	language,
	onToggle,
	onLanguageChange,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`liPost.${key}`);

	const languageOptions = useMemo(
		() =>
			JOB_CHANGE_LANGUAGES.map((lang) => ({
				title: `${LANGUAGE_LABELS[lang]} — "${LANGUAGE_PHRASES[lang]}"`,
				value: lang,
			})),
		[]
	);

	const handleSelect = (value: SelectFieldValue) => {
		if (value) {
			onLanguageChange(value as JobChangeLanguage);
		}
	};

	return (
		<div
			className={classNames(
				"my-5 rounded-md border bg-white px-3.5 py-3",
				enabled ? "border-brand-light bg-gray-100" : "border-border-light"
			)}
		>
			<div className="flex items-center gap-2.5">
				<Toggle isChecked={enabled} testId="job-change-toggle" onChange={onToggle} />

				<div className="flex items-center justify-center w-[26px] h-[26px] shrink-0 rounded-md bg-light-blue text-blue-action">
					<Icon className="text-[13px]" icon={faBriefcase} />
				</div>

				<div className="flex-1 min-w-0">
					<div className="text-[13px] font-semibold text-gray-900">{ts("jobChangeTitle")}</div>
					<div className="text-[11.5px] leading-snug text-text-meta">{ts("jobChangeDesc")}</div>
				</div>
			</div>

			{enabled && (
				<div className="flex items-center gap-2.5 mt-2.5 pt-2.5 border-t border-dashed border-border-light">
					<span className="text-[12px] font-medium text-gray-700 shrink-0">
						{ts("jobChangeLanguageLabel")}
					</span>
					<div className="flex-1">
						<SelectField
							hideEmptyValue
							handleSelect={handleSelect}
							options={languageOptions}
							selectedOption={language}
						/>
					</div>
				</div>
			)}
		</div>
	);
};
